/**
 * <colorway-morph> — two-colorway packshot switcher (sections/colorway-morph.liquid).
 * CSS owns layout and visuals (stacked layers, opacity, swatch states). This element only
 * moves the state carriers: aria-pressed on the swatches, data-visible on the layers and
 * data-crossfade on itself while the fallback fade runs.
 *
 * Click on the inactive swatch:
 *   1. aria-pressed switches at once
 *   2. video path — the transition video of the colorway being LEFT (index 0 plays 1→2,
 *      index 1 plays 2→1) is rewound and played; on `playing` it replaces the outgoing
 *      packshot (instant swap: its first frame matches); on `ended` the target packshot is
 *      decoded, then replaces the video (instant swap: the last frame matches)
 *   3. fallback — no video, readyState < HAVE_FUTURE_DATA, play() rejected or
 *      prefers-reduced-motion: the two packshots crossfade (--cm-fade, 300ms)
 * Clicks on the active swatch or during a transition are ignored, not queued.
 *
 * The initial colorway is server-rendered (starting_colorway setting): the pressed swatch
 * sets `active` and the layers are synced to it on connect.
 *
 * Videos ship with preload="none"; the first time the section comes within one viewport
 * (IntersectionObserver, rootMargin 100%) both switch to preload="auto" and load().
 *
 * Everything is block-scoped inside the customElements guard (featured-technologies.ts
 * pattern): components compile as classic scripts sharing one global scope.
 */
if (!customElements.get('colorway-morph')) {
    const REF = {
        packshot: '[data-ref="packshot"]',
        video: '[data-ref="video"]',
        swatch: '[data-ref="swatch"]',
    };
    /** Videos start loading one viewport before the section is visible. */
    const PRELOAD_ROOT_MARGIN = '100% 0px';
    /** HTMLMediaElement.HAVE_FUTURE_DATA */
    const MIN_READY_STATE = 3;
    /** Safety net for the crossfade transitionend (--cm-fade is 300ms). */
    const FADE_FALLBACK_MS = 400;
    /** Added to the video duration before giving up on `ended` (stalled network). */
    const VIDEO_END_GRACE_MS = 2000;
    const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
    class ColorwayMorph extends HTMLElement {
        packshots = [];
        /** Transition video keyed by the index of the colorway it leaves. */
        videos = new Map();
        swatches = [];
        active = 0;
        busy = false;
        abort = null;
        observer = null;
        connectedCallback() {
            this.abort = new AbortController();
            this.collect();
            this.resetLayers();
            this.addEventListener('click', this.handleClick, { signal: this.abort.signal });
            this.observePreload();
        }
        disconnectedCallback() {
            this.abort?.abort();
            this.abort = null;
            this.observer?.disconnect();
            this.observer = null;
            this.videos.forEach((video) => video.pause());
            // A transition cut short by removal never resumes: its awaits check the aborted signal
            this.busy = false;
        }
        // --- setup ----------------------------------------------------------------
        collect() {
            this.packshots = Array.from(this.querySelectorAll(REF.packshot));
            this.swatches = Array.from(this.querySelectorAll(REF.swatch));
            this.videos.clear();
            this.querySelectorAll(REF.video).forEach((video) => {
                // Belt and braces: autoplay policies check the muted property
                video.muted = true;
                this.videos.set(Number(video.dataset.index), video);
            });
            const pressed = this.swatches.find((swatch) => swatch.getAttribute('aria-pressed') === 'true');
            this.active = pressed ? Number(pressed.dataset.index) : 0;
        }
        /** Sync the layers with `active`: the server-rendered state, or a transition cut short by a DOM move. */
        resetLayers() {
            delete this.dataset.crossfade;
            this.packshots.forEach((layer) => this.setVisible(layer, Number(layer.dataset.index) === this.active));
            this.videos.forEach((video) => this.setVisible(video, false));
        }
        observePreload() {
            if (this.videos.size === 0)
                return;
            this.observer = new IntersectionObserver((entries, observer) => {
                if (!entries.some((entry) => entry.isIntersecting))
                    return;
                observer.unobserve(this);
                this.videos.forEach((video) => {
                    // preload first: load() re-runs resource selection with the new hint
                    video.preload = 'auto';
                    video.load();
                });
            }, { rootMargin: PRELOAD_ROOT_MARGIN });
            this.observer.observe(this);
        }
        // --- interaction -----------------------------------------------------------
        handleClick = (event) => {
            const swatch = event.target.closest(REF.swatch);
            if (!swatch)
                return;
            const target = Number(swatch.dataset.index);
            if (this.busy || target === this.active)
                return;
            this.transitionTo(target);
        };
        async transitionTo(target) {
            const signal = this.abort?.signal;
            if (!signal)
                return;
            const from = this.active;
            this.busy = true;
            this.active = target;
            this.swatches.forEach((swatch) => swatch.setAttribute('aria-pressed', String(Number(swatch.dataset.index) === target)));
            const played = await this.playTransition(from, target, signal);
            if (signal.aborted)
                return;
            if (!played)
                await this.crossfade(from, target, signal);
            if (signal.aborted)
                return;
            this.busy = false;
        }
        /**
         * Video path. Resolves false while nothing has changed on screen yet, so the crossfade
         * can take over; true once the video has run (or the element went away).
         */
        async playTransition(from, target, signal) {
            const video = this.videos.get(from);
            if (!video || video.readyState < MIN_READY_STATE)
                return false;
            if (window.matchMedia(REDUCED_MOTION_QUERY).matches)
                return false;
            video.currentTime = 0;
            const playing = this.listen(video, ['playing'], signal);
            try {
                await video.play();
            }
            catch {
                playing.cancel();
                return false;
            }
            await playing.done;
            if (signal.aborted)
                return true;
            // First video frame = outgoing packshot: instant swap
            this.setVisible(video, true);
            this.setVisible(this.packshots[from], false);
            const duration = Number.isFinite(video.duration) ? video.duration * 1000 : 0;
            await this.within(this.listen(video, ['ended', 'error'], signal), duration + VIDEO_END_GRACE_MS);
            if (signal.aborted)
                return true;
            await this.decode(target);
            if (signal.aborted)
                return true;
            // Last video frame = target packshot: instant swap back
            this.setVisible(this.packshots[target], true);
            this.setVisible(video, false);
            return true;
        }
        /** Fallback: straight opacity crossfade between the two packshots. */
        async crossfade(from, target, signal) {
            await this.decode(target);
            if (signal.aborted)
                return;
            const incoming = this.packshots[target];
            const faded = this.listen(incoming, ['transitionend', 'transitioncancel'], signal);
            // The transition rule and the opacity change land in the same style update; the
            // change still animates because transitions use the after-change style
            this.dataset.crossfade = 'true';
            this.setVisible(incoming, true);
            this.setVisible(this.packshots[from], false);
            await this.within(faded, FADE_FALLBACK_MS);
            if (signal.aborted)
                return;
            delete this.dataset.crossfade;
        }
        // --- helpers ---------------------------------------------------------------
        /** Decode the target packshot before showing it, so the swap never shows an empty frame. */
        async decode(index) {
            const img = this.packshots[index]?.querySelector('img');
            if (!img)
                return; // theme editor placeholder (svg)
            try {
                await img.decode();
            }
            catch {
                // Broken or unloadable image: show the layer anyway rather than stay stuck
            }
        }
        setVisible(layer, visible) {
            if (layer)
                layer.dataset.visible = String(visible);
        }
        /**
         * Settles on the first of `types` fired by `target`, or on the element's abort —
         * whichever comes first. cancel() drops the listeners without settling.
         */
        listen(target, types, signal) {
            let cancel = () => { };
            const done = new Promise((resolve) => {
                const settle = () => {
                    cancel();
                    resolve();
                };
                cancel = () => {
                    types.forEach((type) => target.removeEventListener(type, settle));
                    signal.removeEventListener('abort', settle);
                };
                types.forEach((type) => target.addEventListener(type, settle));
                signal.addEventListener('abort', settle);
            });
            return { done, cancel };
        }
        /** Waits for `listener`, at most `ms` (safety net); its listeners are dropped either way. */
        async within(listener, ms) {
            let timer = 0;
            await Promise.race([
                listener.done,
                new Promise((resolve) => {
                    timer = window.setTimeout(resolve, ms);
                }),
            ]);
            window.clearTimeout(timer);
            listener.cancel();
        }
    }
    customElements.define('colorway-morph', ColorwayMorph);
}
